import { Folder, FolderMinus, FolderOpen, Images, type LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { useMediaFolders } from '../hooks/use-media-folders';
import { UNFILED } from '../schemas/media-list-search';

export interface MediaFolderRailProps {
  /** A folder id, `UNFILED`, or undefined for every file. */
  value: string | undefined;
  onChange: (folder: string | undefined) => void;
  className?: string;
}

/** The picker's folder list, file-manager style; it hides itself while the organization has no folders. */
export function MediaFolderRail({ value, onChange, className }: MediaFolderRailProps) {
  const { t } = useTranslation('media');
  const folders = useMediaFolders();
  if (folders.isSuccess && folders.data.length === 0) return null;
  if (folders.isError) return null;

  const entry = (key: string | undefined, label: string, icon: LucideIcon, count?: number) => {
    const active = value === key;
    const Icon = active && icon === Folder ? FolderOpen : icon;
    return (
      <li key={key ?? '__all__'}>
        <button
          type="button"
          aria-current={active ? 'true' : undefined}
          onClick={() => onChange(key)}
          className={cn(
            'hover:bg-muted focus-visible:ring-ring/50 flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-sm outline-none focus-visible:ring-[3px]',
            active && 'bg-muted font-medium',
          )}
        >
          <Icon
            className={cn('size-4 shrink-0', active ? 'text-foreground' : 'text-muted-foreground')}
            aria-hidden
          />
          <span className="min-w-0 flex-1 truncate">{label}</span>
          {count === undefined ? null : (
            <span className="text-muted-foreground text-xs tabular-nums">{count}</span>
          )}
        </button>
      </li>
    );
  };

  return (
    <nav aria-label={t('folders.title')} className={cn('flex flex-col gap-1 p-3', className)}>
      <ul className="flex flex-col gap-0.5">
        {entry(undefined, t('folders.all'), Images)}
        {entry(UNFILED, t('folders.unfiled'), FolderMinus)}
      </ul>
      <p className="text-muted-foreground px-2.5 pt-3 pb-1 text-xs font-medium tracking-wide uppercase">
        {t('folders.title')}
      </p>
      {folders.isPending ? (
        <div className="flex flex-col gap-1.5 px-2.5">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-6 w-full" />
          ))}
        </div>
      ) : (
        <ul className="flex flex-col gap-0.5">
          {folders.data.map((folder) => entry(folder.id, folder.name, Folder, folder.itemCount))}
        </ul>
      )}
    </nav>
  );
}
